import { Cell, Modal } from '@telegram-apps/telegram-ui'
import { useMutation } from '@tanstack/react-query'
import { useTranslationChange } from 'i18nano'
import { Check, Globe } from 'lucide-react'
import { useState } from 'react'
import { useMobileTrpc } from 'shared/api'
import { useTranslate } from 'shared/lib/i18n'
import {
	DEFAULT_LANGUAGE,
	isUserLanguage,
	type UserLanguage,
} from 'shared/lib/i18n/languages'
import { updateStorage } from 'shared/lib/storage'
import { useAuthStore } from 'shared/store/auth'

const LANGUAGE_OPTIONS: ReadonlyArray<{
	language: UserLanguage
	label: string
	flag: string
}> = [
	{ language: 'ru', label: 'Русский', flag: '/assets/ru.svg' },
	{ language: 'uz', label: "O'zbekcha", flag: '/assets/uz.svg' },
]

export function LanguageSwitcher() {
	const trpc = useMobileTrpc()
	const { t } = useTranslate()
	const { lang, change } = useTranslationChange()
	const [open, setOpen] = useState(false)
	const updateLanguage = useMutation({
		...trpc.profile.SetLanguage.mutationOptions(),
		scope: { id: 'profile-language' },
	})
	const currentLanguage = isUserLanguage(lang) ? lang : DEFAULT_LANGUAGE
	const currentOption = LANGUAGE_OPTIONS.find(
		(option) => option.language === currentLanguage,
	)!

	function selectLanguage(language: UserLanguage) {
		if (language === currentLanguage) {
			setOpen(false)
			return
		}
		change(language)
		updateStorage('user-preferences', { language })

		const user = useAuthStore.getState().user
		useAuthStore.getState().setUser({ ...user, language })
		updateLanguage.mutate({ language })
		setOpen(false)
	}

	return (
		<>
			<Cell
				Component='button'
				type='button'
				className='w-full text-left'
				aria-label={`${t('common.language')}: ${currentOption.label}`}
				onClick={() => setOpen(true)}
				before={<Globe aria-hidden size={22} />}
			>
				{t('common.language')}: {currentOption.label}
			</Cell>

			<Modal
				open={open}
				onOpenChange={setOpen}
				aria-label={t('common.language')}
				header={<Modal.Header>{t('common.language')}</Modal.Header>}
			>
				<div className='flex w-full flex-col items-stretch pb-8'>
					{LANGUAGE_OPTIONS.map((option) => (
						<Cell
							key={option.language}
							Component='button'
							type='button'
							className='w-full items-center text-left'
							aria-current={
								option.language === currentLanguage ? 'true' : undefined
							}
							onClick={() => selectLanguage(option.language)}
							before={
								<img
									src={option.flag}
									alt=''
									className='h-4 w-6 object-cover'
								/>
							}
							after={
								option.language === currentLanguage ? (
									<Check aria-hidden size={20} />
								) : undefined
							}
						>
							{option.label}
						</Cell>
					))}
				</div>
			</Modal>
		</>
	)
}
